import React, { useState, useEffect, useCallback } from 'react';
import chemicalService from '../../../services/chemicalService';

const ProductionFormModal = ({ onClose, onSaved }) => {
  const [formulas, setFormulas] = useState([]);
  const [loadingFormulas, setLoadingFormulas] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Form fields
  const [formulaId, setFormulaId] = useState('');
  const [batchNo, setBatchNo] = useState('');
  const [productionDate, setProductionDate] = useState(new Date().toISOString().slice(0, 10));
  const [multiplier, setMultiplier] = useState('1');
  const [expiryDate, setExpiryDate] = useState('');
  const [actualOutput, setActualOutput] = useState('');
  const [notes, setNotes] = useState('');

  // Selected formula details + ingredient rows
  const [formula, setFormula] = useState(null);
  const [loadingFormula, setLoadingFormula] = useState(false);
  // each row: { product_id, product_name, product_unit, baseQuantity, quantity, batchId, batches: [], loadingBatches }
  const [rows, setRows] = useState([]);

  // ─── Load formula dropdown ───────────────────────────────
  useEffect(() => {
    const loadFormulas = async () => {
      try {
        setLoadingFormulas(true);
        const res = await chemicalService.getFormulas({ is_active: true, limit: 200 });
        setFormulas(res.data || []);
      } catch (err) {
        console.error('Error loading formulas:', err);
        alert('❌ Failed to load recipes');
      } finally {
        setLoadingFormulas(false);
      }
    };
    loadFormulas();
  }, []);

  // ─── Expected output (from formula yield × multiplier) ───
  const expectedOutput = formula
    ? (parseFloat(formula.yield_qty) || 0) * (parseFloat(multiplier) || 1)
    : 0;

  // ─── When formula or multiplier changes, rebuild ingredient rows ───
  const loadFormulaDetails = useCallback(async (id, mult) => {
    if (!id) {
      setFormula(null);
      setRows([]);
      return;
    }
    try {
      setLoadingFormula(true);
      const res = await chemicalService.getFormulaById(id);
      const f = res.data;
      setFormula(f);

      const m = parseFloat(mult) || 1;

      // Build a row per ingredient, then fetch available batches for each
      const baseRows = (f.ingredients || []).map((ing) => ({
        product_id: ing.product_id,
        product_name: ing.product_name,
        product_code: ing.product_code,
        product_unit: ing.product_unit || ing.unit,
        baseQuantity: parseFloat(ing.quantity) || 0,
        quantity: ((parseFloat(ing.quantity) || 0) * m).toString(),
        batchId: '',
        batches: [],
        loadingBatches: true,
      }));
      setRows(baseRows);

      // Fetch batches for each ingredient product
      const withBatches = await Promise.all(
        baseRows.map(async (row) => {
          try {
            const bRes = await chemicalService.getBatchesByProduct(row.product_id);
            const batches = bRes.data || [];
            return {
              ...row,
              batches,
              // auto-select first available batch if any
              batchId: batches.length > 0 ? String(batches[0].id) : '',
              loadingBatches: false,
            };
          } catch (e) {
            console.error('Error loading batches for product', row.product_id, e);
            return { ...row, batches: [], loadingBatches: false };
          }
        })
      );
      setRows(withBatches);
    } catch (err) {
      console.error('Error loading formula details:', err);
      alert('❌ Failed to load recipe details');
      setFormula(null);
      setRows([]);
    } finally {
      setLoadingFormula(false);
    }
  }, []);

  // Load details when formula changes
  useEffect(() => {
    loadFormulaDetails(formulaId, multiplier);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formulaId]);

  // When multiplier changes, just rescale quantities (don't refetch batches)
  useEffect(() => {
    const m = parseFloat(multiplier) || 1;
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        quantity: (r.baseQuantity * m).toString(),
      }))
    );
    // also default the actual output to expected when multiplier changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiplier]);

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  // ─── Submit ──────────────────────────────────────────────
  const handleSubmit = async () => {
    // Validation
    if (!formulaId) return alert('⚠️ Please select a recipe');
    if (!batchNo.trim()) return alert('⚠️ Please enter an output batch number');
    if (!productionDate) return alert('⚠️ Please select a production date');
    if (!actualOutput || parseFloat(actualOutput) <= 0) return alert('⚠️ Please enter actual output (greater than 0)');
    if (rows.length === 0) return alert('⚠️ This recipe has no ingredients');

    // Build consumed array, validating each row
    const consumed = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.batchId) {
        return alert(`⚠️ Please select a batch for "${r.product_name}"`);
      }
      const qty = parseFloat(r.quantity);
      if (!qty || qty <= 0) {
        return alert(`⚠️ Quantity for "${r.product_name}" must be greater than 0`);
      }
      // Check the selected batch has enough stock
      const batch = r.batches.find((b) => String(b.id) === String(r.batchId));
      if (batch && parseFloat(batch.qty_available) < qty) {
        return alert(
          `⚠️ Not enough stock in batch ${batch.batch_no} for "${r.product_name}".\n` +
          `Required: ${qty} ${r.product_unit}, Available: ${batch.qty_available} ${r.product_unit}`
        );
      }
      consumed.push({
        batch_id: parseInt(r.batchId),
        product_id: r.product_id,
        quantity: qty,
      });
    }

    const payload = {
      batch_no: batchNo.trim(),
      formula_id: parseInt(formulaId),
      production_date: productionDate,
      multiplier: parseFloat(multiplier) || 1,
      actual_output: parseFloat(actualOutput),
      expiry_date: expiryDate || null,
      notes: notes.trim() || null,
      consumed,
    };

    try {
      setSubmitting(true);
      await chemicalService.createProduction(payload);
      alert('✅ Production completed! Output batch created and raw materials deducted.');
      onSaved();
    } catch (err) {
      console.error('Error creating production:', err);
      alert('❌ ' + (err.response?.data?.message || 'Failed to create production'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>⚗ Run New Production</h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              Execute a recipe — raw stock will be deducted and a new finished batch created
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={{ padding: '24px 30px' }}>

          {/* Setup section */}
          <div style={styles.sectionLabel}>Setup</div>
          <div style={styles.formGrid}>
            <div>
              <label style={styles.label}>Recipe <span style={styles.req}>*</span></label>
              <select
                value={formulaId}
                onChange={(e) => setFormulaId(e.target.value)}
                style={styles.input}
                disabled={loadingFormulas}
              >
                <option value="">{loadingFormulas ? 'Loading recipes...' : '-- Select recipe --'}</option>
                {formulas.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.code} — {f.product_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={styles.label}>Output Batch No. <span style={styles.req}>*</span></label>
              <input
                type="text"
                value={batchNo}
                onChange={(e) => setBatchNo(e.target.value)}
                placeholder="e.g. CHM-2026-029"
                style={styles.input}
              />
            </div>

            <div>
              <label style={styles.label}>Production Date <span style={styles.req}>*</span></label>
              <input
                type="date"
                value={productionDate}
                onChange={(e) => setProductionDate(e.target.value)}
                style={styles.input}
              />
            </div>

            <div>
              <label style={styles.label}>Multiplier</label>
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={multiplier}
                onChange={(e) => setMultiplier(e.target.value)}
                style={styles.input}
              />
              <div style={styles.hint}>How many times to run the recipe (1 = single batch, 2 = double)</div>
            </div>

            <div>
              <label style={styles.label}>Output Expiry Date</label>
              <input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                style={styles.input}
              />
            </div>

            <div>
              <label style={styles.label}>
                Actual Output <span style={styles.req}>*</span>
                {formula && (
                  <span style={styles.expectedHint}> (expected {expectedOutput.toFixed(2).replace(/\.?0+$/, '')} {formula.product_unit})</span>
                )}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={actualOutput}
                onChange={(e) => setActualOutput(e.target.value)}
                placeholder={formula ? expectedOutput.toFixed(2) : 'Select recipe first'}
                style={styles.input}
              />
            </div>
          </div>

          {/* Ingredients / batch picker */}
          {loadingFormula ? (
            <div style={{ padding: '30px', textAlign: 'center', color: '#7f8c8d' }}>
              ⏳ Loading recipe ingredients...
            </div>
          ) : formula && rows.length > 0 ? (
            <>
              <div style={{ ...styles.sectionLabel, marginTop: '24px' }}>
                Raw Materials to Consume (auto-deducted on save)
              </div>
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.headerRow}>
                      <th style={styles.th}>Material</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Required Qty</th>
                      <th style={styles.th}>Pick Batch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.product_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={styles.td}>
                          <div style={{ fontWeight: '600' }}>{r.product_name}</div>
                          <div style={{ fontSize: '12px', color: '#95a5a6' }}>{r.product_code}</div>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={r.quantity}
                            onChange={(e) => updateRow(i, 'quantity', e.target.value)}
                            style={styles.qtyInput}
                          />
                          <span style={{ marginLeft: '6px', color: '#7f8c8d', fontSize: '13px' }}>{r.product_unit}</span>
                        </td>
                        <td style={styles.td}>
                          {r.loadingBatches ? (
                            <span style={{ color: '#95a5a6', fontSize: '13px' }}>Loading batches...</span>
                          ) : r.batches.length === 0 ? (
                            <span style={{ color: '#e74c3c', fontSize: '13px', fontWeight: '600' }}>
                              ⚠️ No stock available
                            </span>
                          ) : (
                            <select
                              value={r.batchId}
                              onChange={(e) => updateRow(i, 'batchId', e.target.value)}
                              style={styles.batchSelect}
                            >
                              <option value="">-- Select batch --</option>
                              {r.batches.map((b) => (
                                <option key={b.id} value={b.id}>
                                  {b.batch_no} (avail: {parseFloat(b.qty_available).toFixed(2).replace(/\.?0+$/, '')} {r.product_unit})
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : formula && rows.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#e74c3c' }}>
              ⚠️ This recipe has no ingredients defined.
            </div>
          ) : null}

          {/* Notes */}
          {formula && (
            <div style={{ marginTop: '20px' }}>
              <label style={styles.label}>Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes about this production run..."
                rows={2}
                style={{ ...styles.input, resize: 'vertical' }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.modalFooter}>
          <button onClick={onClose} style={styles.cancelButton} disabled={submitting}>
            Cancel
          </button>
          <button onClick={handleSubmit} style={styles.submitButton} disabled={submitting || !formula}>
            {submitting ? '⏳ Processing...' : '⚗ Start Production'}
          </button>
        </div>

      </div>
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white', borderRadius: '12px',
    width: '95%', maxWidth: '900px', maxHeight: '92vh',
    overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '20px 30px', borderBottom: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    position: 'sticky', top: 0, background: 'white', zIndex: 10,
  },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: {
    padding: '8px 16px', background: '#f0f0f0', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    fontSize: '20px', fontWeight: '700', color: '#666',
  },
  sectionLabel: {
    fontSize: '12px', fontWeight: '700',
    color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px',
    marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid #f0f0f0',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '16px',
  },
  label: {
    display: 'block', fontSize: '13px', fontWeight: '600',
    color: '#2c3e50', marginBottom: '6px',
  },
  req: { color: '#e74c3c' },
  expectedHint: { fontWeight: '400', color: '#7f8c8d', fontSize: '12px' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', fontFamily: 'inherit', boxSizing: 'border-box',
  },
  hint: { fontSize: '11px', color: '#95a5a6', marginTop: '4px', fontStyle: 'italic' },
  tableWrap: {
    border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#f8f9fa' },
  th: {
    padding: '12px 14px', textAlign: 'left',
    fontSize: '12px', fontWeight: '700',
    color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.3px',
  },
  td: { padding: '12px 14px', fontSize: '14px', color: '#2c3e50' },
  qtyInput: {
    width: '90px', padding: '6px 8px',
    border: '2px solid #e0e0e0', borderRadius: '6px',
    fontSize: '14px', textAlign: 'right', fontFamily: 'inherit',
  },
  batchSelect: {
    width: '100%', padding: '8px 10px',
    border: '2px solid #e0e0e0', borderRadius: '6px',
    fontSize: '13px', fontFamily: 'inherit',
  },
  modalFooter: {
    padding: '16px 30px', borderTop: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'flex-end', gap: '12px',
    position: 'sticky', bottom: 0, background: 'white',
  },
  cancelButton: {
    padding: '12px 24px', background: '#95a5a6', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
  submitButton: {
    padding: '12px 24px', background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
};

export default ProductionFormModal;